import React, { useRef, useState, useEffect } from 'react';
import suitcase from '../../images/suitcase.svg';
import calendar from '../../images/calendar.svg';
import EmployerCard from '../../Components/EmployerCard';
import ImagesViewer from '../../Components/ImageElements/ImagesViewer';
import ImageDownloadable from '../../Components/ImageElements/ImageDownloadable';
import { Carousel, Image, TourProps } from 'antd';
import { TextPreparator } from '../../utils/TextPreparator';
import { Company, Vacancy as VacancyType } from '../../types';
import { LoaderWithTimer } from '../../Components/LoaderWithTimer';
import { UserTour } from '../../Components/UserTour';

const getMonthName = (monthIndex: string) => {
    switch (monthIndex) {
      case '01' : return 'января'
      case '02' : return 'февраля'
      case '03' : return 'марта'
      case '04' : return 'апреля'
      case '05' : return 'мая'
      case '06' : return 'июня'
      case '07' : return 'июля'
      case '08' : return 'августа'
      case '09' : return 'сентября'
      case '10' : return 'октября'
      case '11' : return 'ноября'
      case '12' : return 'декабря'
    }
  }

type VacancyProps = {
    userRole: string
    children?: React.ReactNode
    vacancy: VacancyType
    isLoading?: boolean
}

export default function Vacancy({vacancy, userRole, isLoading, children}: VacancyProps) {
    const [name, setName] = useState('');
    const [status, setStatus] = useState<string | undefined>('');
    const [salary, setSalary] = useState<string | undefined>('');
    const [shortDescription, setShortDescription] = useState<string | undefined>('');
    const [fullDescription, setFullDescription] = useState<string | undefined>('');

    const [isProbation, setIsProbation] = useState(false);

    const [icon, setIcon] = useState(suitcase);
    const [educationOrDates, setEducationOrDates] = useState('');
 
    const [company, setCompany] = useState<Company>();
    const [imagesIds, setImageIds] = useState<string[]>([]);

    const [addressVacancy, setAddressVacancy] = useState('');

    const companyRef = useRef(null);

    const steps: TourProps['steps'] =
        company ? [
            {
                title: 'О компании',
                description: 'Нажмите на карточку, чтобы перейти на страницу компании',
                target: () => companyRef.current
            }] 
        : [];

    const internshipDatesToString = (start?: any, end?: any) => {
        let sd, ed;
        if (start) {
            start = String(start).split('T')
            sd = start[0].split('-')
        }
        if (end) {
            end = String(end).split('T')
            ed = end[0].split('-')
        }
        if (!sd && !ed) {
            setEducationOrDates('Даты стажировки не указаны');
        }
        if (sd && ed) {
            setEducationOrDates(`С ${sd[2]} ${getMonthName(sd[1])} по ${ed[2]} ${getMonthName(ed[1])}`)
        }
    } 
    
    useEffect(() => {
        if (vacancy) {
            setName(vacancy.name)
            setStatus(vacancy.status)
            setSalary(vacancy.salary.toString())
            setShortDescription(vacancy.shortDescription)
            setFullDescription(TextPreparator.prepare(vacancy.fullDescription as string))
            setIsProbation(vacancy.isProbation)
    
            if (vacancy.isProbation) {
                internshipDatesToString(vacancy.internshipStartDate, vacancy.internshipEndDate)
                setIcon(calendar)
            } else {
                setEducationOrDates(`Требуется: ${vacancy.education}`)
            }

            if (vacancy.address) {
                const { region, city, street, house } = vacancy.address;
                if (city && street && house) {
                    setAddressVacancy(`${city}, ${street}, ${house}`)
                } else if (region || city || street || house) {
                    setAddressVacancy(`${region} ${city} ${street} ${house}`);
                } else {
                    setAddressVacancy('Адрес не указан');
                }
            }
            setCompany(vacancy.company)
            setImageIds(vacancy.company?.imagesIds || [])
        }
    }, [vacancy, isLoading]);

    return (
        <div className="left-page-container">
            <div className="left-page-content" >
                <div style={{borderBottom: '0.5px solid #D1D6DC', marginBottom:'32px'}}>
                    {!isLoading ? 
                        <div style={{ maxWidth: '690px' }}>  
                            <div className="header-card" style={{marginBottom:'8px'}}>
                                <h1 style={{display:'inline-block', marginRight: '8px'}}> {name} </h1>
                                {(isProbation) && <div className="probation-vacancyCard"> Стажировка </div>}
                                {(status === "Опубликована" && userRole === "employer") && <div className="published-vacancy"> {status} </div>}
                                {(status === "В архиве") && <div className="inArchiveStatus"> {status} </div>}
                            </div>
                            <h3 style={{ paddingBottom: '12px' }}> {salary} </h3>
                            <p style={{marginBottom:'12px'}}>
                            <img src={icon} width="16px" style={{marginTop:'-3px', marginRight:'5px'}}/>
                                {educationOrDates}
                            </p>
                            <p style={{marginBottom:'24px'}}> 
                                {shortDescription}
                            </p>
                            {children}
                        </div> 
                        : 
                        <LoaderWithTimer isLoading={isLoading} skeletonStyle="vacancy"/>
                    }
                </div>
                <div className="d-sm-block d-md-none">
                    {company && (
                        <EmployerCard company={company as Company} address={addressVacancy} />
                    )}
                </div>
                <div dangerouslySetInnerHTML={{ __html: fullDescription || '' }} />
                <Image.PreviewGroup items={imagesIds}>
                    <Carousel 
                        infinite={false}
                        className="d-sm-block d-md-none mobile-carousel"
                        style={{height: '300px', marginTop: '10px'}}>
                        {imagesIds && imagesIds.map(image => (
                            <ImageDownloadable 
                                id={image}
                                key={image}
                                type={'company'} 
                            />
                        ))}
                    </Carousel>
                </Image.PreviewGroup>
            </div>
            <div className="right-page-content d-none d-md-block">
                <div ref={companyRef}>
                    <EmployerCard company={company as Company} address={addressVacancy}/>
                </div>
                <ImagesViewer imagesIds={imagesIds} type={'company'}/>
            </div>
            {userRole === "profile" && <UserTour steps={steps} pageKey="vacancy" />}
        </div>
    );
};